'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import type { ObjectComponent } from '@sizzlenz/shared';
import { ObjectComponentForm } from './ObjectComponentForm';
import { ObjectComponentItem } from './ObjectComponentItem';

function generateId(): string {
  return crypto.randomUUID();
}

interface ObjectComponentsEditorProps {
  value: ObjectComponent[];
  onChange: (components: ObjectComponent[]) => void;
}

export function ObjectComponentsEditor({ value, onChange }: ObjectComponentsEditorProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleRemove = (index: number) => {
    const next = [...value];
    next.splice(index, 1);
    onChange(next);
  };

  const handleSave = (data: Omit<ObjectComponent, 'id'>) => {
    const next = [...value];
    if (editingIndex !== null) {
      next[editingIndex] = { ...next[editingIndex], ...data };
    } else {
      next.push({ id: generateId(), ...data });
    }
    onChange(next);
    setEditingIndex(null);
  };

  const editingComponent = editingIndex !== null ? value[editingIndex] : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-lg font-medium">Components</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="cursor-pointer"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add...
        </Button>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">
          No components added yet. Add requirements like venue, catering, etc.
        </p>
      )}

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((component, index) => (
            <ObjectComponentItem
              key={component.id}
              component={component}
              onEdit={() => handleEdit(index)}
              onRemove={() => handleRemove(index)}
            />
          ))}
        </div>
      )}

      <ObjectComponentForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialData={editingComponent}
        onSave={handleSave}
      />
    </div>
  );
}
